import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { ArrowDown, ArrowUp, ArrowUpDown, Search } from 'lucide-react'
import { fetchCustomers } from '@/api/customers'
import { fetchDefenderDetections } from '@/api/defender'
import { CustomerFilter } from '@/components/CustomerFilter'
import { DefenderNav } from '@/components/defender/DefenderNav'
import { Badge } from '@/components/ui/badge'
import { Card, CardContent } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useAuth } from '@/auth/AuthContext'
import { formatDateTime } from '@/lib/formatAgent'
import { cn } from '@/lib/utils'

const PAGE_SIZE = 50

const SORTABLE = new Set([
  'occurred_at',
  'hostname',
  'customer',
  'threat_name',
  'severity',
  'category',
])

function severityVariant(severity) {
  const text = String(severity || '').toLowerCase()
  if (text === 'severe' || text === 'high') return 'destructive'
  if (text === 'moderate') return 'warning'
  if (text === 'low') return 'secondary'
  return 'secondary'
}

function SortHeader({ column, label, sortBy, sortDir, onSort, className }) {
  const active = sortBy === column
  if (!SORTABLE.has(column)) {
    return <th className={cn('px-4 py-3 font-medium', className)}>{label}</th>
  }
  return (
    <th
      className={cn(
        'px-4 py-3 font-medium cursor-pointer select-none hover:text-foreground',
        className,
      )}
      onClick={() => onSort(column)}
    >
      <span className="inline-flex items-center gap-1">
        {label}
        {active ? (
          sortDir === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5 shrink-0" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5 shrink-0" />
          )
        ) : (
          <ArrowUpDown className="h-3.5 w-3.5 shrink-0 opacity-40" />
        )}
      </span>
    </th>
  )
}

export function DefenderDetections() {
  const { isCustomer, canFilterCustomers } = useAuth()
  const [customers, setCustomers] = useState([])
  const [customerIds, setCustomerIds] = useState([])
  const [items, setItems] = useState([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pages, setPages] = useState(1)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [severity, setSeverity] = useState('')
  const [sortBy, setSortBy] = useState('occurred_at')
  const [sortDir, setSortDir] = useState('desc')

  useEffect(() => {
    if (canFilterCustomers) {
      fetchCustomers().then(setCustomers).catch(() => {})
    }
  }, [canFilterCustomers])

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(searchInput.trim())
      setPage(1)
    }, 350)
    return () => clearTimeout(t)
  }, [searchInput])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const data = await fetchDefenderDetections(
        {
          page,
          page_size: PAGE_SIZE,
          search: search || undefined,
          severity: severity || undefined,
          sort_by: sortBy,
          sort_dir: sortDir,
          days: 90,
        },
        customerIds,
      )
      setItems(data.items)
      setTotal(data.total)
      setPages(data.pages)
    } catch {
      setError('Could not load Defender detections.')
      setItems([])
    } finally {
      setLoading(false)
    }
  }, [page, search, customerIds, severity, sortBy, sortDir])

  useEffect(() => {
    load()
  }, [load])

  function handleSort(column) {
    if (!SORTABLE.has(column)) return
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))
    } else {
      setSortBy(column)
      setSortDir(column === 'occurred_at' ? 'desc' : 'asc')
    }
    setPage(1)
  }

  return (
    <div className="space-y-6">
      <DefenderNav />

      <p className="text-sm text-muted-foreground">
        Windows Defender threat detections from the last 30 days (defender-detections RMM script).
        Empty until the script is deployed and has reported.
      </p>

      {error && <p className="text-sm text-destructive">{error}</p>}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {canFilterCustomers && (
          <CustomerFilter
            id="defender-detections-customer"
            value={customerIds}
            onChange={(v) => {
              setCustomerIds(v)
              setPage(1)
            }}
            customers={customers}
          />
        )}
        <div className="space-y-2">
          <Label htmlFor="defender-detections-search">Search</Label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              id="defender-detections-search"
              placeholder="Threat, path, hostname…"
              className="pl-9"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="defender-severity">Severity</Label>
          <select
            id="defender-severity"
            className="flex h-10 w-full rounded-md border border-input bg-secondary/50 px-3 text-sm"
            value={severity}
            onChange={(e) => {
              setSeverity(e.target.value)
              setPage(1)
            }}
          >
            <option value="">All severities</option>
            <option value="Severe">Severe</option>
            <option value="High">High</option>
            <option value="Moderate">Moderate</option>
            <option value="Low">Low</option>
            <option value="Unknown">Unknown</option>
          </select>
        </div>
      </div>

      {loading ? (
        <Skeleton className="h-64 w-full" />
      ) : items.length === 0 ? (
        <Card>
          <CardContent className="pt-6 text-sm text-muted-foreground">
            No Defender detections in the lookback window. Deploy{' '}
            <code className="text-xs">defender-detections.ps1</code> from Atera (run as System) to
            populate this tab.
          </CardContent>
        </Card>
      ) : (
        <div className="rounded-md border border-border overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border bg-muted/40 text-left text-xs text-muted-foreground">
                <SortHeader
                  column="occurred_at"
                  label="Detected"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  column="hostname"
                  label="Device"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                {canFilterCustomers && (
                  <SortHeader
                    column="customer"
                    label="Customer"
                    sortBy={sortBy}
                    sortDir={sortDir}
                    onSort={handleSort}
                  />
                )}
                <SortHeader
                  column="threat_name"
                  label="Threat"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  column="severity"
                  label="Severity"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortHeader
                  column="category"
                  label="Category"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th className="px-4 py-3 font-medium">Path / process</th>
                <th className="px-4 py-3 font-medium">Action</th>
              </tr>
            </thead>
            <tbody>
              {items.map((row) => (
                <tr key={row.id} className="border-b border-border last:border-0 align-top">
                  <td className="px-4 py-3 whitespace-nowrap tabular-nums">
                    {formatDateTime(row.occurred_at)}
                  </td>
                  <td className="px-4 py-3">
                    {row.agent_id ? (
                      <Link
                        to={`/agents/${row.agent_id}?tab=security`}
                        className="font-medium text-primary hover:underline"
                      >
                        {row.display_name || row.hostname || row.computer}
                      </Link>
                    ) : (
                      <span className="font-medium">{row.hostname || row.computer}</span>
                    )}
                  </td>
                  {canFilterCustomers && (
                    <td className="px-4 py-3 text-muted-foreground">
                      {row.customer_name || '—'}
                    </td>
                  )}
                  <td className="px-4 py-3">
                    <p className="font-medium">{row.threat_name || '—'}</p>
                    {row.threat_id ? (
                      <p className="text-xs text-muted-foreground">ID {row.threat_id}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    {row.severity ? (
                      <Badge variant={severityVariant(row.severity)}>{row.severity}</Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                  <td className="px-4 py-3">{row.category || '—'}</td>
                  <td className="px-4 py-3">
                    <p className="max-w-xs truncate" title={row.path || undefined}>
                      {row.path || '—'}
                    </p>
                    {row.process_name ? (
                      <p className="text-xs text-muted-foreground">{row.process_name}</p>
                    ) : null}
                  </td>
                  <td className="px-4 py-3">
                    {row.action_success === true ? (
                      <Badge variant="success">Succeeded</Badge>
                    ) : row.action_success === false ? (
                      <Badge variant="destructive">Failed</Badge>
                    ) : (
                      '—'
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {pages > 1 && (
        <div className="flex items-center justify-between text-sm">
          <p className="text-muted-foreground">
            Page {page} of {pages} · {total} detection{total === 1 ? '' : 's'}
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1 disabled:opacity-40"
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              Previous
            </button>
            <button
              type="button"
              className="rounded-md border border-border px-3 py-1 disabled:opacity-40"
              disabled={page >= pages}
              onClick={() => setPage((p) => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
